import PropTypes from 'prop-types';
import React, { memo } from 'react';
import classNames from 'classnames';
import { Map, fromJS } from 'immutable';
import config from 'src/config';
import getPercent from 'src/utils/getPercent';
import Button from 'src/components/helpers/Button';
import { IconButton } from 'src/components/helpers/IconButton';

import { CompanyBuyerPlatformProjectTable } from './CompanyBuyerPlatformProjectTable';

import style from './style.module.css';

const platformColumnDefs = config.tables.getIn(['companyBuyerProjects', 'platform', 'columnDefs']);
const addonColumnDefs = config.tables.getIn(['companyBuyerProjects', 'addon', 'columnDefs']);

const getPercentByApproved = (value, data, item) =>
  `${value} (${getPercent(data[item], Number(data.dsplApprovedCount))})%`;

const updateDataTable = dataTable =>
  dataTable
    .get('queryResults')
    .toJS()
    .map(data => {
      const newData = {};

      Object.keys(data).forEach(item => {
        if (item === 'numberOfContacted') {
          newData.numberOfContacted = getPercentByApproved(data.numberOfContacted, data, item);
        }

        if (item === 'numberOfLeads') {
          newData.numberOfLeads = getPercentByApproved(data.numberOfLeads, data, item);
        }

        if (item === 'numberOfCc') {
          newData.numberOfCc = getPercentByApproved(data.numberOfCc, data, item);
        }

        if (item === 'numberOfVisit') {
          newData.numberOfVisit = getPercentByApproved(data.numberOfVisit, data, item);
        }

        if (item === 'numberOfOffers') {
          newData.numberOfOffers = getPercentByApproved(data.numberOfOffers, data, item);
        }

        if (item === 'numberOfLois') {
          newData.numberOfLois = getPercentByApproved(data.numberOfLois, data, item);
        }

        if (item === 'numberOfClosed') {
          newData.numberOfClosed = getPercentByApproved(data.numberOfClosed, data, item);
        }
      });

      return { ...data, ...newData };
    });

const ActiveCellRenderer = ({ value }) => (
  <div className="approvedWrapper">{value ? <i className="text-success fa fa-check" /> : null}</div>
);

const getColumnDefs = platformColumnDefs =>
  platformColumnDefs.map(columnDef => {
    const field = columnDef.get('field');

    switch (field) {
      case 'active':
        return columnDef.set('cellRendererFramework', ActiveCellRenderer);

      default:
        return columnDef;
    }
  });

const CompanyBuyerProjectsComponent = ({
  canEditData,
  projects,
  onDoubleClickRow,
  onGenerateReport,
  onGetNextPageDataForPlatform,
  onGetNextPageDataForAddon,
  onPlatformChange,
  onAddonChange,
  onAddPlatformProject,
}) => {
  const platform = projects.get('platform');
  const addOn = projects.get('addon');

  const dataPlatform = platform.set('queryResults', fromJS(updateDataTable(platform)));
  const dataAddon = projects.set('queryResults', fromJS(updateDataTable(addOn)));

  return (
    <div className={classNames(style.host, 'flexWrapper full-height')}>
      <CompanyBuyerPlatformProjectTable
        actions={
          <Button className={classNames(style.report, 'btn-link')} onClick={onGenerateReport}>
            Client Summary Report
          </Button>
        }
        canEditData={canEditData}
        columnDefs={getColumnDefs(platformColumnDefs)}
        data={dataPlatform}
        onChange={onPlatformChange}
        onDoubleClickRow={onDoubleClickRow}
        onGetNextPageData={onGetNextPageDataForPlatform}
        title="Platform Projects"
      >
        <IconButton
          className={style.add}
          icon="plus"
          onClick={onAddPlatformProject}
          size="sm"
          title="Add platform project"
          variant="primary"
        />
      </CompanyBuyerPlatformProjectTable>
      <CompanyBuyerPlatformProjectTable
        canEditData={canEditData}
        columnDefs={addonColumnDefs}
        data={dataAddon}
        onChange={onAddonChange}
        onDoubleClickRow={onDoubleClickRow}
        onGetNextPageData={onGetNextPageDataForAddon}
        title="Add-On Projects"
      />
    </div>
  );
};

CompanyBuyerProjectsComponent.propTypes = {
  canEditData: PropTypes.bool.isRequired,
  onAddonChange: PropTypes.func.isRequired,
  onAddPlatformProject: PropTypes.func.isRequired,
  onDoubleClickRow: PropTypes.func.isRequired,
  onGenerateReport: PropTypes.func.isRequired,
  onGetNextPageDataForAddon: PropTypes.func.isRequired,
  onGetNextPageDataForPlatform: PropTypes.func.isRequired,
  onPlatformChange: PropTypes.func.isRequired,
  projects: PropTypes.instanceOf(Map).isRequired,
};

/** Company buyer projects component. */
export const CompanyBuyerProjects = memo(CompanyBuyerProjectsComponent);
